import React from 'react';
import { Github, Linkedin, ArrowDown } from 'lucide-react';
import GradientButton from './ui/GradientButton';
import ScrollReveal from './ui/ScrollReveal';
import { trackEvent } from '../utils/analytics';

export const Hero: React.FC = () => {
  return (
    <section id="home" className="min-h-screen flex items-center relative pt-20">
      <div className="absolute inset-0 -z-10 aurora" aria-hidden="true" />
      <div className="grain" aria-hidden="true" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <ScrollReveal className="order-2 lg:order-1">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-4 tracking-tight">
              <span className="block text-ink">Hi, I'm</span>
              <span className="bg-gradient-to-r from-brand-gold1 to-brand-gold2 bg-clip-text text-transparent">Sudipta Dhara</span>
            </h1>
            <p className="text-xl md:text-2xl text-ink-dim mb-4 max-w-2xl">
              Software Developer • Generative AI Engineer • Backend Developer
            </p>
            <p className="text-lg text-ink-dim mb-2 max-w-2xl">
              Specializing in RAG pipelines, Agentic AI, LangChain, LangGraph, and production LLM applications
            </p>
            <p className="text-base text-ink-dim mb-8 max-w-2xl">
              Based in Kolkata, West Bengal, India • 14+ Years of Experience
            </p>
            <p className="text-sm text-ink-dim mb-8 max-w-2xl">
              Learn more about <a href="#about" className="text-brand hover:underline">Sudipta Dhara's experience and expertise</a> or explore <a href="#products" className="text-brand hover:underline">products developed by Sudipta Dhara</a>.
            </p>
            <div className="flex flex-wrap gap-4 mb-8">
              <GradientButton 
                href="#contact"
                onClick={() => trackEvent('hero-contact-me')}
                data-umami-event="hero-contact-me"
              >
                Contact Me
              </GradientButton>
              <a 
                href="#about" 
                className="inline-flex items-center justify-center rounded-mdx px-6 py-3 border border-white/20 text-ink/90 hover:text-ink bg-white/5 hover:bg-white/10 transition-colors"
                onClick={() => trackEvent('hero-learn-more')}
                data-umami-event="hero-learn-more"
              >
                Learn More
              </a>
            </div>
            <div className="flex gap-4">
              <a 
                href="https://www.linkedin.com/in/sudipta26889" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-ink/80 hover:text-brand transition-colors" 
                aria-label="LinkedIn Profile"
                onClick={() => trackEvent('social-linkedin')}
                data-umami-event="social-linkedin"
              >
                <Linkedin size={24} />
              </a>
              <a 
                href="https://github.com/sudipta26889" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-ink/80 hover:text-ink transition-colors" 
                aria-label="GitHub Profile"
                onClick={() => trackEvent('social-github')}
                data-umami-event="social-github"
              >
                <Github size={24} />
              </a>
            </div>
          </ScrollReveal>
          <ScrollReveal className="order-1 lg:order-2 flex justify-center">
            <div className="relative w-64 h-64 md:w-80 md:h-80 rounded-full overflow-hidden border border-white/20 glass">
              <img
                src="/sudipta.jpeg"
                alt="Sudipta Dhara - Software Developer and Generative AI Engineer from Kolkata, India"
                width={320}
                height={320}
                className="w-full h-full object-cover"
                loading="eager"
                fetchPriority="high"
              />
            </div>
          </ScrollReveal>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <a 
          href="#about" 
          aria-label="Scroll down" 
          className="inline-flex items-center justify-center"
          onClick={() => trackEvent('hero-scroll-down')}
          data-umami-event="hero-scroll-down"
        >
          <ArrowDown size={28} className="text-ink/70" />
        </a>
      </div>
    </section>
  );
};
